import { useQuickActionFromUrl } from "components/GlobalSearch/useQuickActionFromUrl";
import { PendingBulkApprovalActions } from "../BulkApprovalBar";
import { usePendingBulkApproval } from "../usePendingBulkApproval";
import { buildLeaveApproval } from "../bulkApprovalRequests";
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import * as Mui from "@mui/material";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { ApprovalDateRangeFilter } from "components/CustomComponents/ApprovalDateRangeFilter";
import { resolveRangeLabel } from "../dateRangeUtils";
import sessionHandling from "services/utils/notificationUtils";
import { customAPI, customGetAPI } from "services/utils/CustomAPI";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { setFilterApi } from "services/Redux/userManagament";
import { StatusRow } from "components/CustomComponents/StatusRow";
import ApprovalAuditTrail from "components/DesignSystem/ApprovalAuditTrail";
import {
  scrollTop,
  getStatusFlow,
  StatusBadge,
  displayFlexItem,
} from "services/constants";
import { Apply } from "./Apply";
import { useSelector, useDispatch } from "react-redux";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { Loader } from "components/Loader";
import { Box } from "@mui/material";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { showToast } from "services/utils/Status";
import { TeamZoneLeave } from "../Headers";
import { LeaveDetailPanel } from "./LeaveDetailPanel";
import { defaultAllSelection } from "services/constants";
import { useSavedHeaderViews } from "components/CustomComponents/useSavedHeaderViews";
import { SavedViewSelector } from "components/CustomComponents/SavedViewSelector";
import { LeaveStatusCards, canonicalLeaveStatus, leaveStatusVisual } from "./LeaveStatusCards";
import { useExpandablePanel } from "services/hooks/useExpandablePanel";
import {
  TruncatedTableValue,
} from "components/DesignSystem/tableColumnSizing";
import {
  APP_BAR_OFFSET,
  designColors,
  designShell,
  designType,
  paginationFooter,
  rowsPerPageOptions,
} from "services/constants/designTokens";
import {
  approveStickyCustomFilterBar,
  approveStickyFooterBar,
  approveTableHeadCell,
  approveTableBodyCell,
  bulkSelectionCheckboxSx,
  NewRequestButton,
  ApproveTable,
} from "../approveUi";
import moment from "moment";

// "Default View" sentinel for the saved-view selector — the default view
// itself shows every column; a curated subset is still available by editing
// it (or creating a new view) via "Add new view" in the selector.
const LEAVE_LIST_DEFAULT_VIEW_ID = "__default_leave_list_view__";

const getLeaveCellValue = (leave, key) => {
  if (key === "username") {
    return leave?.employee_name || leave?.username || "N/A";
  }

  if (key === "requested_date") {
    return leave?.attendance_type === "Comp off"
      ? (leave?.requested_date ?? "N/A")
      : (leave?.date ?? "N/A");
  }

  return leave?.[key] ?? "N/A";
};

export const Leave = ({
  setReCallApi,
  reCallApi,
  startDate,
  endDate,
  dateRangeLabel: dateRangeLabelProp,
  handleStartDate,
  subModuleId,
}) => {
  const {
    domain,
    userDetails,
    token,
    topbarCompanyId,
    topbarCompanyName,
    userScreenAccess,
  } = useSelector((state) => state.tokenReducer);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const containerRef = useRef(null);
  // Measures the sticky footer bar's own height so the table can reserve
  // that much bottom padding — otherwise its last row would sit hidden
  // behind the footer once the page scrolls that footer's sticky trigger
  // into effect.
  const [footerNode, setFooterNode] = useState(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const footerRef = useCallback((node) => setFooterNode(node), []);
  // Same measurement Administration's ListView/Companies' ListView use —
  // CustomFilter's chip row only appears once a filter/sort is applied, so
  // the sticky table head below it needs this measured (not guessed) to
  // know how far down to offset itself, matching Admin's page-level
  // sticky-header pattern instead of the boxed/own-scrollbar approach this
  // screen used before it had this measurement available.
  const [customFilterNode, setCustomFilterNode] = useState(null);
  const [customFilterHeight, setCustomFilterHeight] = useState(0);
  const customFilterRef = useCallback((node) => setCustomFilterNode(node), []);
  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filteredValue, setFilteredValue] = useState("");
  // The current leave year's id, needed by LeaveDetailPanel's Cancel Leave
  // flow (CancelLeaveDialog's cancel_create_leave_request/ call requires a
  // leave_year_id) — same field MyZone's own Leave screen tracks as
  // selectedYearId from this same leaves-years/ response.
  const [selectedYearId, setSelectedYearId] = useState(null);
  const [leaveData, setLeaveData] = useState([]);
  const [leaveFilterData, setLeaveFilterData] = useState([]);
  const [viewLeaveDetails, setViewLeaveDetails] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  // Inline per-row timeline expand (chevron in the leading column) — same
  // pattern as OT Pre-Approval/Off-Day Pre-Approval's tables, kept separate
  // from selectedData/viewLeaveDetails above since that pair opens the
  // side LeaveDetailPanel and this only toggles a row within the table.
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [expandedData, setExpandedData] = useState(null);
  const [statusCardFilter, setStatusCardFilter] = useState("");
  // Initialized from (and kept in sync with) the Approval Center landing
  // page's own dateRangeLabel prop, rather than a hardcoded "Last 7 Days"
  // — that stayed wrong whenever this screen was opened directly (a fresh
  // ?view=leave deep link, or Team Workspace Home's "date" param) with
  // startDate/endDate not actually matching the last-7-days range. Manually
  // re-picking a range here (handleLeaveDateRange below) still overrides
  // this locally afterward.
  const [dateRangeLabel, setDateRangeLabel] = useState(
    dateRangeLabelProp || "Last 7 Days",
  );

  useEffect(() => {
    if (dateRangeLabelProp) setDateRangeLabel(dateRangeLabelProp);
  }, [dateRangeLabelProp]);
  const { expanded: leavePanelExpanded, toggleExpanded: toggleLeavePanelExpanded } =
    useExpandablePanel(viewLeaveDetails);
  const {
    expanded: createLeavePanelExpanded,
    toggleExpanded: toggleCreateLeavePanelExpanded,
  } = useExpandablePanel(isCreatePanelOpen);
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchLeaveDetails = async () => {
      const res = await customGetAPI(
        `${domain}leave-request/?user_id=${userDetails.id}&department_id=${defaultAllSelection}&selected_user_id=${defaultAllSelection}&start_date=${startDate}&end_date=${endDate}&company_id=${topbarCompanyId}`,
      );
      const rows = (res?.responseDetails || []).map((row) => ({
        ...row,
        // getStatusFlow (services/constants) already reads these same
        // fields for the expand row's approval timeline — one column
        // showing whichever of the three actually fired for this row.
        approver_name:
          row.approved_by || row.rejected_by || row.cancelled_by || "-",
      }));
      setLeaveData(rows);
      // Seed the filtered view with these same rows — CustomFilter
      // recomputes leaveFilterData in its own effect, which fires a render
      // after this one. Without this, the commit where `loading` flips off
      // still has leaveFilterData at its old/empty value, so the table
      // flashes "No Matching Results" for a frame before the real rows land.
      setLeaveFilterData(
        rows.map((leave) => ({ ...leave, reason: leave.applied_reason })),
      );
      setLoading(false);
      setReCallApi(false);
    };
    if (startDate && endDate && reCallApi) {
      setLoading(true);
      fetchLeaveDetails();
    }
  }, [
    domain,
    endDate,
    reCallApi,
    setReCallApi,
    startDate,
    topbarCompanyId,
    userDetails.id,
  ]);

  let check_access = checkEditorPermission(
    userScreenAccess?.module_details,
    "Approvals",
    "Leaves",
  );

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  useEffect(() => {
    if (!footerNode) return;
    const updateHeight = () => setFooterHeight(footerNode.offsetHeight);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(footerNode);
    return () => observer.disconnect();
  }, [footerNode]);

  useEffect(() => {
    if (!customFilterNode) return;
    const updateHeight = () =>
      setCustomFilterHeight(customFilterNode.offsetHeight);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(customFilterNode);
    return () => observer.disconnect();
  }, [customFilterNode]);

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setLeaveFilterData(
        filteredData.map((leave) => ({
          ...leave,
          reason: leave.applied_reason,
        })),
      );
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage],
  );

  // Tallies for the Pending/Approved/Rejected/Cancelled cards, computed from
  // the full date-range fetch (leaveData) rather than leaveFilterData — so
  // the counts stay stable while the user searches/sorts via CustomFilter,
  // the same way Admin's UserStatCards bases its counts on the unfiltered
  // master list rather than the currently-filtered view.
  const leaveStatusCounts = useMemo(
    () =>
      (leaveFilterData || []).reduce((acc, request) => {
        const status = canonicalLeaveStatus(request?.status);
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {}),
    [leaveFilterData],
  );

  // Layered on top of whatever CustomFilter itself produced, driven by the
  // status cards above — mirrors Admin's ListView `visibleUsers`.
  const visibleLeaveData = useMemo(() => {
    if (!statusCardFilter) return leaveFilterData;
    return leaveFilterData.filter(
      (request) => canonicalLeaveStatus(request?.status) === statusCardFilter,
    );
  }, [leaveFilterData, statusCardFilter]);

  const bulkApproval = usePendingBulkApproval({
    rows: visibleLeaveData,
    canonicalStatus: canonicalLeaveStatus,
    canApprove: check_access && statusCardFilter === "Pending",
    approveRow: (row) => customAPI(
      `${domain}leave_request_update/?company_id=${topbarCompanyId}`,
      "PUT",
      buildLeaveApproval(row, userDetails),
    ),
    rejectRow: (row, reason) => customAPI(
      `${domain}leave_request_update/?company_id=${topbarCompanyId}`,
      "PUT",
      buildLeaveApproval(row, userDetails, "Rejected", reason),
    ),
    onApproved: () => setReCallApi(true),
  });

  const handleStatusCardClick = (key) => {
    setPage(0);
    setExpandedIndex(null);
    setExpandedData(null);
    setViewLeaveDetails(false);
    setSelectedData(null);
    if (key === "All") {
      setStatusCardFilter("");
    } else {
      setStatusCardFilter(key);
    }
  };

  // Search quick action opens the list filtered to Pending requests.
  useQuickActionFromUrl({ "approve-leave": () => handleStatusCardClick("Pending") }, !loading);

  // Same shape as the Approval Center landing page's own
  // handleApprovalCenterDateRange — feeds the same handleStartDate prop
  // this screen already receives from its parent, just with this screen's
  // own local dateRangeLabel instead of the landing page's.
  const handleLeaveDateRange = (rangeStart, rangeEnd, rangeLabel) => {
    handleStartDate(rangeStart, rangeEnd);
    setDateRangeLabel(resolveRangeLabel(rangeLabel));
  };

  const handleCreateClosePanel = () => {
    setIsCreatePanelOpen(false);
  };

  const handleSavedViewChange = useCallback(() => {
    setPage(0);
  }, []);

  const leaveColumnHeaders = useMemo(
    () => TeamZoneLeave.filter((header) => header.key !== "expand"),
    [],
  );

  // Default View / saved-view selector — same shape as Administration's
  // Users list (ListView.js). Replaces the earlier "Save View" chip row
  // (useSaveView + SavedViewChips + SaveViewButton), which Admin's own user
  // list never had; this is the actual view-management UI it uses instead.
  const {
    savedViewsForScope,
    activeViewId,
    selectView,
    selectDefaultView,
    deleteView,
    createView,
    upsertView,
    updateView,
  } = useSavedHeaderViews({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "team_leave_saved_views",
    scope: "default",
    onViewChange: handleSavedViewChange,
    defaultViewId: LEAVE_LIST_DEFAULT_VIEW_ID,
  });
  const persistedDefaultViewId = activeViewId || LEAVE_LIST_DEFAULT_VIEW_ID;
  const [currentViewId, setCurrentViewId] = useState(null);

  useEffect(() => {
    setCurrentViewId((prev) => {
      if (prev && prev !== LEAVE_LIST_DEFAULT_VIEW_ID) {
        return prev;
      }
      return persistedDefaultViewId;
    });
  }, [persistedDefaultViewId]);

  const baseDefaultHeaders = useMemo(() => leaveColumnHeaders, [leaveColumnHeaders]);

  const storedDefaultView = useMemo(
    () =>
      savedViewsForScope.find((view) => view.id === LEAVE_LIST_DEFAULT_VIEW_ID) ||
      null,
    [savedViewsForScope],
  );

  const customSavedViews = useMemo(
    () =>
      savedViewsForScope.filter((view) => view.id !== LEAVE_LIST_DEFAULT_VIEW_ID),
    [savedViewsForScope],
  );

  const defaultView = useMemo(() => {
    if (storedDefaultView?.isCustomDefault) {
      // Whatever headerConfig the user saved is authoritative — same as any
      // other saved view. (Previously this back-filled any header missing
      // from headerConfig, meant to surface columns added to the schema
      // after the view was saved — but that's indistinguishable from a
      // column the user deliberately unchecked, so it silently re-added
      // every unchecked header right back on the next render.)
      return storedDefaultView;
    }

    return {
      id: LEAVE_LIST_DEFAULT_VIEW_ID,
      name: "Default View",
      headerConfig: baseDefaultHeaders.map((header) => ({
        key: header.key,
        name: header.name,
      })),
      headerKeys: baseDefaultHeaders.map((header) => header.key),
      isDefaultView: true,
    };
  }, [baseDefaultHeaders, storedDefaultView]);

  const effectiveViewId = currentViewId ?? persistedDefaultViewId;

  const activeView = useMemo(() => {
    if (effectiveViewId === LEAVE_LIST_DEFAULT_VIEW_ID) {
      return defaultView;
    }

    return (
      customSavedViews.find((view) => view.id === effectiveViewId) ||
      defaultView
    );
  }, [customSavedViews, defaultView, effectiveViewId]);

  const visibleHeaders = useMemo(() => {
    const headerByKey = new Map(
      leaveColumnHeaders.map((header) => [header.key, header]),
    );

    if (activeView?.headerConfig?.length) {
      const orderedHeaders = activeView.headerConfig
        .map((entry) => {
          const baseHeader = headerByKey.get(entry.key);
          if (!baseHeader) return null;
          return {
            ...baseHeader,
            name: entry.name?.trim() || baseHeader.name,
          };
        })
        .filter(Boolean);

      return orderedHeaders.length ? orderedHeaders : baseDefaultHeaders;
    }

    if (activeView?.headerKeys?.length) {
      const selectedKeys = new Set(activeView.headerKeys);
      const filteredHeaders = leaveColumnHeaders.filter((header) =>
        selectedKeys.has(header.key),
      );
      return filteredHeaders.length ? filteredHeaders : baseDefaultHeaders;
    }

    return baseDefaultHeaders;
  }, [activeView, baseDefaultHeaders, leaveColumnHeaders]);

  const handleSelectView = useCallback(
    (viewId) => {
      setCurrentViewId(viewId);
      handleSavedViewChange();
    },
    [handleSavedViewChange],
  );

  const handleSelectDefaultView = useCallback(() => {
    setCurrentViewId(LEAVE_LIST_DEFAULT_VIEW_ID);
    handleSavedViewChange();
  }, [handleSavedViewChange]);

  const handleMakeDefaultView = useCallback(
    (viewId) => {
      if (viewId === LEAVE_LIST_DEFAULT_VIEW_ID) {
        selectDefaultView();
        setCurrentViewId(LEAVE_LIST_DEFAULT_VIEW_ID);
        return;
      }

      selectView(viewId);
      setCurrentViewId(viewId);
    },
    [selectDefaultView, selectView],
  );

  const handleCreateView = useCallback(
    (viewName, headerConfig) => {
      createView(viewName, headerConfig, { activate: false });
    },
    [createView],
  );

  const handleDeleteView = useCallback(
    (viewId) => {
      if (currentViewId === viewId) {
        setCurrentViewId(LEAVE_LIST_DEFAULT_VIEW_ID);
      }
      deleteView(viewId);
    },
    [currentViewId, deleteView],
  );

  const handleEditDefaultView = useCallback(
    (viewId, viewName, headerConfig) => {
      upsertView(
        viewId || LEAVE_LIST_DEFAULT_VIEW_ID,
        viewName || "Default View",
        headerConfig,
        "Default view updated successfully.",
        { isCustomDefault: true },
      );
      setCurrentViewId(LEAVE_LIST_DEFAULT_VIEW_ID);
    },
    [upsertView],
  );

  const titleOverride = useMemo(
    () => (
      <SavedViewSelector
        headers={leaveColumnHeaders}
        defaultHeaders={baseDefaultHeaders}
        defaultView={defaultView}
        savedViews={customSavedViews}
        activeView={activeView}
        activeViewId={effectiveViewId}
        persistedDefaultViewId={persistedDefaultViewId}
        screenAccess={check_access}
        onSelectDefaultView={handleSelectDefaultView}
        onSelectView={handleSelectView}
        onCreateView={handleCreateView}
        onUpdateView={(viewId, viewName, headerConfig) => {
          if (viewId === LEAVE_LIST_DEFAULT_VIEW_ID) {
            handleEditDefaultView(viewId, viewName, headerConfig);
            return;
          }
          updateView(viewId, viewName, headerConfig);
        }}
        onDeleteView={handleDeleteView}
        onMakeDefaultView={handleMakeDefaultView}
      />
    ),
    [
      activeView,
      baseDefaultHeaders,
      check_access,
      customSavedViews,
      defaultView,
      effectiveViewId,
      handleCreateView,
      handleDeleteView,
      handleEditDefaultView,
      handleMakeDefaultView,
      handleSelectDefaultView,
      handleSelectView,
      leaveColumnHeaders,
      persistedDefaultViewId,
      updateView,
    ],
  );

  const reportMetadata = useMemo(
    () => ({
      "Company Name": topbarCompanyName || "N/A",
      "Date Range": `${moment(startDate).format("DD/MM/YYYY")} to ${moment(endDate).format("DD/MM/YYYY")}`,
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    }),
    [endDate, startDate, topbarCompanyName],
  );

  // Same width as Admin Users' "Create User" header button
  // (HEADER_ACTION_BUTTON_WIDTH via pageHeaderRow) — this used to override
  // to minWidth:0 and its own shorter minHeight, which is exactly the kind
  // of one-off drift the shared header-button pattern exists to prevent.
  const newLeaveButton = (
    <NewRequestButton
      hasAccess={check_access}
      deniedMessage="Access denied: You are not authorized to create a leave request"
      onClick={() => setIsCreatePanelOpen(true)}
    />
  );

  useEffect(() => {
    const fetchLeaveDate = async () => {
      try {
        const response = await fetch(`${domain}leaves-years/`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `token ${token}`,
          },
        });
        const res = await response.json();
        if (response.ok) {
          const results = res.results;
          setFilteredValue(
            `${results[0]?.start_date} - ${results[0]?.end_date}`,
          );
          setSelectedYearId(results[0]?.id);
          dispatch(setFilterApi(true));
        } else if (response.status === 409) {
          sessionHandling();
        } else {
          throw new Error("REQUEST_FAILED");
        }
      } catch (error) {
        console.error("[Index] operation failed:", error);
        showToast(
          "error",
          "Couldn't load leave details. Please refresh the page.",
        );
      }
    };
    fetchLeaveDate();
  }, [dispatch, domain, token]);

  return loading ? (
    <Loader />
  ) : (
    <React.Fragment>
      <Box
        sx={{
          flexGrow: 1,
          width: "100%",
          // Was overflow:"hidden" — that clips any position:sticky
          // descendant's reference frame (the sticky filter bar/table head/
          // footer below need an unclipped ancestor chain up to the actual
          // page scroll). The table itself uses tableLayout:"fixed" (see
          // its own sx comment) so it never needs its own horizontal
          // scrollbar the way this comment used to describe.
          overflow: "visible",
        }}
      >
        <Box
          sx={{
            mb: 2,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Mui.Typography sx={designType.pageTitle}>Leave</Mui.Typography>
            <Mui.Typography sx={designType.pageSubtitle}>
              Review and manage leave requests awaiting approval
            </Mui.Typography>
          </Box>

          <Mui.Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <ApprovalDateRangeFilter
              startDate={startDate}
              endDate={endDate}
              dateRangeLabel={dateRangeLabel}
              onChange={handleLeaveDateRange}
              includeCurrentDate
              showToday
              allowFutureDate
              sx={{ minHeight: 46 }}
            />
            {newLeaveButton}
          </Mui.Stack>
        </Box>

        <LeaveStatusCards
          counts={leaveStatusCounts}
          activeFilter={statusCardFilter}
          onCardClick={handleStatusCardClick}
        />

        <Box ref={customFilterRef} sx={approveStickyCustomFilterBar}>
          <CustomFilter
            title={"Leave Requests"}
            titleOverride={titleOverride}
            filterHeaders={visibleHeaders}
            data={leaveData}
            filteredData={visibleLeaveData}
            onFilteredDataChange={handleFilteredDataChange}
            screenAccess={check_access}
            hideCount={true}
            downloadFormats={["excel", "pdf", "csv"]}
            includeMetadata={true}
            metadata={reportMetadata}
            labeledToolbar={true}
          
          />
        </Box>
        <PendingBulkApprovalActions selection={bulkApproval} />
        <Box sx={{ ...designShell.sectionCard, mt: 1.5, borderRadius: 0 }}>
          <Mui.Box
            sx={{
              display: { xs: "none", md: "block" },
              position: "sticky",
              top: APP_BAR_OFFSET + customFilterHeight,
              zIndex: 2,
              overflow: "hidden",
              backgroundColor: designColors.white,
            }}
          >
            <Mui.Box sx={{ overflow: "hidden" }}>
              <Mui.Table
                sx={{
                  minWidth: 920,
                  width: "100%",
                  tableLayout: "fixed",
                }}
              >
                <Mui.TableHead sx={designShell.tableStickyHead}>
                  <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                  {statusCardFilter === "Pending" && (
                    <Mui.TableCell padding="checkbox" sx={{ ...approveTableHeadCell, width: 48, position: "static" }}>
                      {bulkApproval.hasPending && (
                        <Mui.Checkbox
                          size="small"
                          sx={bulkSelectionCheckboxSx}
                          checked={bulkApproval.isAllSelected}
                          indeterminate={bulkApproval.isIndeterminate}
                          disabled={bulkApproval.loading}
                          onChange={bulkApproval.toggleAll}
                          inputProps={{ "aria-label": "Select all pending requests" }}
                        />
                      )}
                    </Mui.TableCell>
                  )}
                    <Mui.TableCell
                      sx={{
                        ...approveTableHeadCell,
                        position: "static",
                        top: "auto",
                        width: 32,
                        px: 0.5,
                      }}
                    />
                    {visibleHeaders?.map((value) => (
                      <Mui.TableCell
                        key={value.key}
                        sx={{
                          ...approveTableHeadCell,
                          position: "static",
                          top: "auto",
                          ...(value.key === "username" && { pl: 0.5 }),
                        }}
                      >
                        <TruncatedTableValue value={value?.name} />
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                </Mui.TableHead>
              </Mui.Table>
            </Mui.Box>
          </Mui.Box>
          <Mui.TableContainer
            ref={containerRef}
            sx={{
              width: "100%",
              // "visible" (not "auto") from sm up — matches Admin's
              // Users/Companies lists: at that size this container never
              // becomes its own scroll box, so it never hijacks the sticky
              // header's containing block away from the actual page. Below
              // sm (real phone widths), overflow flips to "auto" so a wide
              // table gets its own contained horizontal scrollbar instead of
              // dragging the whole page sideways; the trade-off is that the
              // sticky header stops pinning to the page at that width and
              // scrolls with the table instead, which is normal for a plain
              // table on a phone.
              overflowX: { xs: "auto", sm: "visible" },
              overflowY: { xs: "auto", sm: "visible" },
              pb: footerHeight ? `${footerHeight}px` : 0,
            }}
          >
              <ApproveTable
                sx={{
                  minWidth: 920,
                  width: "100%",
                  tableLayout: "fixed",
                }}
              >
                <Mui.TableBody>
                  {visibleLeaveData?.length === 0 ? (
                    <TableEmptyState
                      dataLength={leaveData}
                      // Include the selection and expand-chevron columns.
                      colSpan={visibleHeaders.length + 1 + (statusCardFilter === "Pending" ? 1 : 0)}
                    />
                  ) : (
                    visibleLeaveData
                      ?.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                      ?.map((value, index) => (
                        <React.Fragment key={value?.id}>
                          <Mui.TableRow
                            onClick={() => {
                              setSelectedData(value);
                              setViewLeaveDetails(true);
                            }}
                            sx={{
                              cursor: "pointer",
                              ...designShell.tableRowHover,
                              ...(viewLeaveDetails && value?.id === selectedData?.id
                                ? designShell.neutralSelectedRow
                                : {}),
                              "&:last-child td": { borderBottom: "none" },
                            }}
                          >
                            {statusCardFilter === "Pending" && (
                              <Mui.TableCell padding="checkbox" sx={{ ...approveTableBodyCell, width: 48 }} onClick={(event) => event.stopPropagation()}>
                              {statusCardFilter === "Pending" && check_access && canonicalLeaveStatus(value.status) === "Pending" && (
                                <Mui.Checkbox
                                  size="small"
                                  sx={bulkSelectionCheckboxSx}
                                  checked={bulkApproval.isSelected(value)}
                                  disabled={bulkApproval.loading}
                                  onChange={() => bulkApproval.toggleRow(value)}
                                  inputProps={{ "aria-label": "Select pending request" }}
                                />
                              )}
                            </Mui.TableCell>
                            )}
                          <Mui.TableCell
                              sx={{
                                ...approveTableBodyCell,
                                width: 32,
                                minWidth: 32,
                                px: 0.5,
                                overflow: "visible",
                                textOverflow: "clip",
                              }}
                              onClick={(event) => {
                                event.stopPropagation();
                                setExpandedIndex((prev) =>
                                  prev === index ? null : index,
                                );
                                setExpandedData(value);
                              }}
                            >
                              <ArrowRightIcon
                                sx={{
                                  color: designColors.slate500,
                                  cursor: "pointer",
                                  transition: "transform 0.2s ease",
                                  transform:
                                    expandedIndex === index
                                      ? "rotate(90deg)"
                                      : "rotate(0deg)",
                                }}
                              />
                            </Mui.TableCell>
                            {visibleHeaders.map((head) => (
                              <Mui.TableCell
                                key={head.key} data-label={head.name}
                                sx={{
                                  ...approveTableBodyCell,
                                  ...(head.key === "username" && { pl: 0.5 }),
                                }}
                              >
                                {head.key === "username" ? (
                                  <TruncatedTableValue
                                    value={
                                      value?.employee_name || value?.username || "N/A"
                                    }
                                  >
                                    <Mui.Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                      sx={{ minWidth: 0, width: "100%" }}
                                    >
                                      <Mui.Avatar
                                        sx={{ width: 28, height: 28 }}
                                        alt={(value?.employee_name || value?.username)
                                          ?.charAt(0)
                                          .toUpperCase()}
                                        src={value?.image_url}
                                      />
                                      <Mui.Typography
                                        noWrap
                                        data-table-value-content
                                        sx={{
                                        // Same condition the row highlight
                                        // above already uses — selectedData
                                        // doesn't reset to null when the
                                        // panel closes, only
                                        // viewLeaveDetails does, so gating
                                        // on the id alone would leave the
                                        // last-viewed row's name bold after
                                        // closing the panel.
                                        fontWeight:
                                          viewLeaveDetails &&
                                          value?.id === selectedData?.id
                                            ? 700
                                            : 500,
                                        color:
                                          viewLeaveDetails &&
                                          value?.id === selectedData?.id
                                            ? designColors.slate900
                                            : "inherit",
                                          fontSize: 14,
                                          minWidth: 0,
                                        }}
                                      >
                                        {value?.employee_name || value?.username || "N/A"}
                                      </Mui.Typography>
                                    </Mui.Stack>
                                  </TruncatedTableValue>
                                ) : head.key === "status" ? (
                                  <Mui.Box sx={displayFlexItem}>
                                    <StatusBadge
                                      color={
                                        value?.color_code ||
                                        leaveStatusVisual(value?.status).accent
                                      }
                                      label={value?.status || "N/A"}
                                    />
                                  </Mui.Box>
                                ) : (
                                  <TruncatedTableValue
                                    value={getLeaveCellValue(value, head.key)}
                                  />
                                )}
                              </Mui.TableCell>
                            ))}
                          </Mui.TableRow>

                          {expandedIndex === index && (
                            <Mui.TableRow>
                              <Mui.TableCell
                                colSpan={visibleHeaders.length + 1 + (statusCardFilter === "Pending" ? 1 : 0)}
                                sx={{
                                  p: 2,
                                  borderBottom: `1px solid ${designColors.slate200}`,
                                }}
                              >
                                {getStatusFlow(expandedData)[
                                  expandedData?.status
                                ]?.map((item, i, arr) => (
                                  <StatusRow
                                    key={i}
                                    status={item.status}
                                    user={item.user}
                                    date={item.date}
                                    showConnector={i !== arr.length - 1}
                                  />
                                ))}
                                <ApprovalAuditTrail record={expandedData} compact />
                              </Mui.TableCell>
                            </Mui.TableRow>
                          )}
                        </React.Fragment>
                      ))
                  )}
                </Mui.TableBody>
              </ApproveTable>
          </Mui.TableContainer>
          <Box ref={footerRef} sx={approveStickyFooterBar}>
            {paginationFooter({
              page,
              rowsPerPage,
              total: visibleLeaveData?.length || 0,
              itemLabel: "leave requests",
              onPageChange: setPage,
              onRowsPerPageChange: handleChangeRowsPerPage,
              rowsPerPageOptions,
            })}
          </Box>
        </Box>
      </Box>
      <Apply
        open={isCreatePanelOpen}
        onClose={() => {
          handleCreateClosePanel();
        }}
        setReCallApi={setReCallApi}
        filteredValue={filteredValue}
        setFilteredValue={setFilteredValue}
        expanded={createLeavePanelExpanded}
        onToggleExpand={toggleCreateLeavePanelExpanded}
      />
      <LeaveDetailPanel
        open={viewLeaveDetails}
        onClose={() => setViewLeaveDetails(false)}
        leave={selectedData}
        checkAccess={check_access}
        setReCallApi={setReCallApi}
        leaveYearId={selectedYearId}
        expanded={leavePanelExpanded}
        onToggleExpand={toggleLeavePanelExpanded}
      />
    </React.Fragment>
  );
};
